import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowUpRight, ArrowDownRight, Minus, History, User, Clock, GitCompare } from "lucide-react";
import {
  useRunAuthors,
  type ReconciliationRun,
} from "@/hooks/useBillingReconciliation";

interface Props {
  runs: ReconciliationRun[] | undefined;
  activeRunId: string | undefined;
  onSelectRun: (id: string) => void;
}

const fmtMoney = (n: number | null | undefined, currency = "USD") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency }).format(n ?? 0);

const fmtDateTime = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });

type DeltaKey =
  | "accuracy_percent"
  | "expected_count"
  | "captured_count"
  | "phantom_count"
  | "duplicate_count"
  | "drift_count"
  | "out_of_window_count"
  | "missed_visit_count"
  | "over_billed_amount"
  | "under_billed_amount"
  | "drift_amount";

const METRICS: { key: DeltaKey; label: string; kind: "count" | "money" | "percent"; higherIs: "good" | "bad" | "neutral" }[] = [
  { key: "accuracy_percent", label: "Accuracy", kind: "percent", higherIs: "good" },
  { key: "over_billed_amount", label: "Over-billed", kind: "money", higherIs: "bad" },
  { key: "under_billed_amount", label: "Under-billed", kind: "money", higherIs: "bad" },
  { key: "drift_amount", label: "Drift amount", kind: "money", higherIs: "bad" },
  { key: "phantom_count", label: "Phantom", kind: "count", higherIs: "bad" },
  { key: "duplicate_count", label: "Duplicates", kind: "count", higherIs: "bad" },
  { key: "drift_count", label: "Drift items", kind: "count", higherIs: "bad" },
  { key: "out_of_window_count", label: "Out-of-window", kind: "count", higherIs: "bad" },
  { key: "missed_visit_count", label: "Missed visits", kind: "count", higherIs: "bad" },
  { key: "expected_count", label: "Expected", kind: "count", higherIs: "neutral" },
  { key: "captured_count", label: "Captured", kind: "count", higherIs: "neutral" },
];

function formatVal(kind: "count" | "money" | "percent", v: number | null | undefined, currency = "USD") {
  if (v == null) return "—";
  if (kind === "money") return fmtMoney(v, currency);
  if (kind === "percent") return `${v}%`;
  return String(v);
}

function DeltaPill({
  delta, kind, higherIs, currency,
}: { delta: number; kind: "count" | "money" | "percent"; higherIs: "good" | "bad" | "neutral"; currency: string }) {
  if (delta === 0) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
        <Minus className="h-3 w-3" /> no change
      </span>
    );
  }
  const up = delta > 0;
  const isGood = higherIs === "neutral" ? null : (up && higherIs === "good") || (!up && higherIs === "bad");
  const tone =
    isGood === null
      ? "text-muted-foreground"
      : isGood
        ? "text-emerald-600 dark:text-emerald-400"
        : "text-destructive";
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  const formatted =
    kind === "money" ? fmtMoney(Math.abs(delta), currency)
      : kind === "percent" ? `${Math.abs(delta).toFixed(1)}pp`
        : String(Math.abs(delta));
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${tone}`}>
      <Icon className="h-3 w-3" />
      {up ? "+" : "−"}{formatted}
    </span>
  );
}

export function ReconciliationRevisionHistory({ runs, activeRunId, onSelectRun }: Props) {
  const [compareId, setCompareId] = useState<string | undefined>();

  const sorted = useMemo(
    () => (runs ?? []).slice().sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    ),
    [runs]
  );

  const authorIds = useMemo(() => sorted.map((r) => r.generated_by), [sorted]);
  const { data: authors } = useRunAuthors(authorIds);

  const active = useMemo(
    () => (runs ?? []).find((r) => r.id === activeRunId) ?? runs?.[0],
    [runs, activeRunId]
  );

  const baseline = useMemo(() => {
    if (!active) return undefined;
    if (compareId) return (runs ?? []).find((r) => r.id === compareId);
    // default: previous run by created_at
    const idx = sorted.findIndex((r) => r.id === active.id);
    if (idx <= 0) return undefined;
    return sorted[idx - 1];
  }, [active, compareId, runs, sorted]);

  if (!runs?.length) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <History className="h-4 w-4" /> Revision history
          <Badge variant="secondary" className="ml-2">{runs.length} run{runs.length === 1 ? "" : "s"}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Timeline */}
        <div className="space-y-2">
          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Saved runs</div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Saved</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead>Saved by</TableHead>
                  <TableHead className="text-right">Accuracy</TableHead>
                  <TableHead className="text-right">Findings</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.slice().reverse().map((r) => {
                  const isActive = active?.id === r.id;
                  const isBaseline = baseline?.id === r.id;
                  const author = r.generated_by ? authors?.[r.generated_by] : null;
                  const totalFindings =
                    r.phantom_count + r.duplicate_count + r.drift_count +
                    r.out_of_window_count + r.missed_visit_count;
                  return (
                    <TableRow key={r.id} className={isActive ? "bg-muted/50" : undefined}>
                      <TableCell className="whitespace-nowrap">
                        <div className="flex items-center gap-1.5 text-sm">
                          <Clock className="h-3 w-3 text-muted-foreground" />
                          {fmtDateTime(r.created_at)}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{r.period_start} → {r.period_end}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5 text-sm">
                          <User className="h-3 w-3 text-muted-foreground" />
                          {author?.name ?? (r.generated_by ? "Loading…" : "System")}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        {r.accuracy_percent != null ? (
                          <Badge variant={r.accuracy_percent >= 95 ? "secondary" : "destructive"}>
                            {r.accuracy_percent}%
                          </Badge>
                        ) : "—"}
                      </TableCell>
                      <TableCell className="text-right text-sm">{totalFindings}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {!isActive && (
                            <Button size="sm" variant="ghost" onClick={() => onSelectRun(r.id)}>
                              View
                            </Button>
                          )}
                          {isActive && <Badge variant="outline">Active</Badge>}
                          {!isActive && (
                            <Button
                              size="sm"
                              variant={isBaseline ? "default" : "outline"}
                              onClick={() => setCompareId(isBaseline ? undefined : r.id)}
                              title="Compare against this run"
                            >
                              <GitCompare className="h-3 w-3" />
                              {isBaseline ? "Baseline" : "Compare"}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Diff */}
        {active && (
          <div className="space-y-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                What changed
              </div>
              <div className="text-xs text-muted-foreground">
                {baseline ? (
                  <>Comparing <span className="font-medium text-foreground">{fmtDateTime(active.created_at)}</span> vs baseline <span className="font-medium text-foreground">{fmtDateTime(baseline.created_at)}</span></>
                ) : (
                  <>First run — no baseline to compare against</>
                )}
              </div>
            </div>
            {baseline ? (
              <div className="overflow-x-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Metric</TableHead>
                      <TableHead className="text-right">Baseline</TableHead>
                      <TableHead className="text-right">Current</TableHead>
                      <TableHead className="text-right">Change</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {METRICS.map((m) => {
                      const b = (baseline as any)[m.key] as number | null;
                      const c = (active as any)[m.key] as number | null;
                      const delta = (c ?? 0) - (b ?? 0);
                      return (
                        <TableRow key={m.key}>
                          <TableCell className="font-medium text-sm">{m.label}</TableCell>
                          <TableCell className="text-right text-sm tabular-nums">
                            {formatVal(m.kind, b, baseline.currency)}
                          </TableCell>
                          <TableCell className="text-right text-sm tabular-nums">
                            {formatVal(m.kind, c, active.currency)}
                          </TableCell>
                          <TableCell className="text-right">
                            <DeltaPill
                              delta={delta}
                              kind={m.kind}
                              higherIs={m.higherIs}
                              currency={active.currency}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground rounded-md border border-dashed p-4">
                Save a second run to see field-by-field deltas, or click <span className="font-medium">Compare</span> on any earlier row to pick a baseline.
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
